import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Chip, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { getLoginUrl, getSignupUrl, getSocialLoginUrl } from '../services/auth';
import { AGENCY_TRIAL_CREDITS, isSelfSignupTier, type SelfSignupTier } from '@traaker/shared';
import { applyUiLanguage } from '../i18n';
import { SIGNUP_LANG_STASH_KEY, normalizeUiLanguage } from '../i18n/languages';

/**
 * Public, pre-auth signup entry — two-column split (auth left, marketing right).
 * Pick a plan upstream on the pricing page (?tier=…); here we just authenticate
 * (Google or email via the Cognito hosted UI). The chosen tier + an optional
 * brand-URL hint are stashed so they survive the IdP redirect and are read back
 * by the post-auth /onboarding step (which also offers the tier picker when no
 * tier was preselected).
 *
 * `?lang=fr|en` (handed off by the marketing site's language switcher) picks
 * the page's own language immediately AND is stashed so the very first
 * onboarding screens (before any per-user preference exists) match the
 * language the visitor was already browsing traaker.ai in.
 *
 * Enterprise is admin-provisioned → "Parler à un expert". Agency self-signs up
 * (no-expiry credit trial); its plan is chosen on the pricing page.
 */
const PLAN_LABELS: Record<SelfSignupTier, { labelKey: string; trialKey: string }> = {
  basic: { labelKey: 'signup.plan.basic', trialKey: 'signup.trial.days7' },
  pro: { labelKey: 'signup.plan.pro', trialKey: 'signup.trial.noCommitment' },
  elite: { labelKey: 'signup.plan.elite', trialKey: 'signup.trial.noCommitment' },
  agency: { labelKey: 'signup.plan.agency', trialKey: 'signup.trial.creditsOffered' },
};

const TIER_STASH_KEY = 'traaker_signup_tier';
const BRAND_STASH_KEY = 'traaker_signup_brand';
/**
 * Ad attribution, stashed on the same rail as tier/brand/lang so it survives
 * the Cognito hosted-UI redirect (which drops all query params). Read back by
 * Onboarding.tsx and posted with the tenant. `gclid` is the Google Ads click
 * id — the `_gcl_aw` cookie is NOT readable here (host-only on traaker.ai),
 * and the click id is what offline-conversion import wants anyway.
 *
 * Requires the marketing site to forward these params into
 * app.traaker.ai/signup — without that there's nothing to capture.
 */
const GCLID_STASH_KEY = 'traaker_signup_gclid';
const UTM_SOURCE_STASH_KEY = 'traaker_signup_utm_source';
const UTM_CAMPAIGN_STASH_KEY = 'traaker_signup_utm_campaign';

const ENGINES: { label: string; src: string }[] = [
  { label: 'ChatGPT', src: '/engines/chatgpt.svg' },
  { label: 'Gemini', src: '/engines/gemini.svg' },
  { label: 'Claude', src: '/engines/claude.svg' },
  { label: 'Perplexity', src: '/engines/perplexity.svg' },
  { label: 'Mistral', src: '/engines/mistral.svg' },
  { label: 'Grok', src: '/engines/grok.svg' },
  { label: 'DeepSeek', src: '/engines/deepseek.svg' },
  { label: 'Meta AI', src: '/engines/meta.svg' },
];
/**
 * Reference brands, same pool (and same rotating-cloud behaviour) as the
 * marketing site's pricing pages — see `initClientCloud()` in website/script.js
 * and the logo-sourcing notes in docs/website/marketing-site.md.
 *
 * The files are the SAME assets as `website/assets/clients/`, normalized for a
 * WHITE page (dark ink). Here they sit on the dark marketing panel, so they're
 * rendered `grayscale(100%) invert(1)`: dark ink becomes light, and the three
 * logos that only exist with an opaque white background (albus, assurance-bebe,
 * staycation) were converted to transparent black-ink PNGs so inversion doesn't
 * leave a black plaque. Check any new logo under BOTH filters before adding it.
 */
const CLIENT_LOGOS: { src: string; alt: string }[] = [
  { src: '/clients/credit-agricole.svg', alt: 'Crédit Agricole' },
  { src: '/clients/atos.svg', alt: 'ATOS' },
  { src: '/clients/uefa-champions-league.svg', alt: 'UEFA Champions League' },
  { src: '/clients/havas-play.webp', alt: 'Havas Play' },
  { src: '/clients/caisse-depargne.svg', alt: 'Caisse d’Épargne' },
  { src: '/clients/staycation.png', alt: 'Staycation' },
  { src: '/clients/sharp.svg', alt: 'Sharp Business' },
  { src: '/clients/arthur-aston.svg', alt: 'Arthur & Aston' },
  { src: '/clients/hedon.svg', alt: 'Hedon Technologies' },
  { src: '/clients/ace-agency.svg', alt: 'Ace Agency' },
  { src: '/clients/albus.png', alt: 'Albus' },
  { src: '/clients/ams-groupe.svg', alt: 'AMS Groupe' },
  { src: '/clients/assurance-bebe.png', alt: 'Assurance-Bébé.ch' },
  { src: '/clients/bibalou.png', alt: 'Bibalou' },
  { src: '/clients/cepovett-safety.png', alt: 'Cepovett Safety' },
  { src: '/clients/elite-irp.png', alt: 'Elite IRP' },
  { src: '/clients/graviti.svg', alt: 'Graviti' },
  { src: '/clients/gustav-immobilier.svg', alt: 'Gustav Immobilier' },
  { src: '/clients/inosys.png', alt: 'Inosys' },
  { src: '/clients/kroqi.svg', alt: 'Kroqi' },
  { src: '/clients/lafont.png', alt: 'Lafont' },
  { src: '/clients/multi-planning.png', alt: 'Multi-Planning' },
  { src: '/clients/myzah.svg', alt: 'Myzah' },
  { src: '/clients/revelez-vos-talents.png', alt: 'Révélez vos talents' },
  { src: '/clients/sharon-gonzalez-avocat.svg', alt: 'Sharon Gonzalez Avocat' },
  { src: '/clients/sia.svg', alt: 'Sia Agents' },
  { src: '/clients/stars-music.svg', alt: 'Stars Music' },
  { src: '/clients/tools-trails.png', alt: 'Tools Trails' },
  { src: '/clients/topaze.svg', alt: 'Topaze' },
  { src: '/clients/treko.svg', alt: 'Treko' },
  { src: '/clients/world-trading-skool.png', alt: 'World Trading Skool' },
];
/** Slots shown at once. The panel is ~54% of the viewport, so fewer than the marketing site's 5. */
const CLIENT_SLOTS = 4;
const CLIENT_FADE_MS = 600;

export default function Signup() {
  const [params] = useSearchParams();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();

  const tierParam = params.get('tier');
  const tier: SelfSignupTier | null =
    tierParam && isSelfSignupTier(tierParam) ? tierParam : null;
  const brandHint = params.get('brand') || '';
  const langParam = params.get('lang');
  const gclid = params.get('gclid') || '';
  const utmSource = params.get('utm_source') || '';
  const utmCampaign = params.get('utm_campaign') || '';

  const [busy, setBusy] = useState(false);

  // Switch this page's own language to match the marketing site immediately
  // (i18n falls back to the browser language otherwise, which may disagree
  // with what the visitor was just reading on traaker.ai).
  useEffect(() => {
    if (langParam) applyUiLanguage(langParam);
  }, [langParam]);

  const stashAndGo = (url: string) => {
    setBusy(true);
    // Persist the plan choice (+ brand hint + language) across the Cognito/IdP redirect.
    // The stash DECIDES the account type post-auth (Onboarding.tsx: agency vs
    // entreprise), so clear it when this visit carries no tier — otherwise an
    // organic signup could inherit a stale agency choice from an earlier visit.
    if (tier) localStorage.setItem(TIER_STASH_KEY, tier);
    else localStorage.removeItem(TIER_STASH_KEY);
    if (brandHint) localStorage.setItem(BRAND_STASH_KEY, brandHint);
    if (langParam) localStorage.setItem(SIGNUP_LANG_STASH_KEY, normalizeUiLanguage(langParam));
    // Ad attribution — only write when present, so a later organic signup in the
    // same browser can't inherit a stale gclid from an earlier ad visit.
    if (gclid) localStorage.setItem(GCLID_STASH_KEY, gclid);
    if (utmSource) localStorage.setItem(UTM_SOURCE_STASH_KEY, utmSource);
    if (utmCampaign) localStorage.setItem(UTM_CAMPAIGN_STASH_KEY, utmCampaign);
    window.location.href = url;
  };

  const state = tier ? `tier:${tier}` : undefined;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left — auth */}
      <Box
        sx={{
          flex: wide ? '0 0 46%' : '1 1 100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 3, sm: 6 },
          py: 6,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Box component="img" src="/traaker-logo-compact.png" alt="Traaker" sx={{ height: 32, mb: 6 }} />

          <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em', mb: 1 }}>
            {t('signup.createAccount')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('signup.subtitle')}
          </Typography>

          {tier && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={t('signup.planChip', {
                plan: t(PLAN_LABELS[tier].labelKey),
                trial: t(PLAN_LABELS[tier].trialKey, { credits: AGENCY_TRIAL_CREDITS }),
              })}
              sx={{ mb: 3, mt: 1, fontWeight: 600 }}
            />
          )}
          <Box sx={{ mb: 3 }} />

          <Button
            variant="outlined"
            fullWidth
            size="large"
            disabled={busy}
            startIcon={<GoogleMark />}
            onClick={() => stashAndGo(getSocialLoginUrl('Google', state))}
            sx={{ mb: 2, py: 1.25, borderColor: 'divider', color: 'text.primary' }}
          >
            {t('signup.continueGoogle')}
          </Button>

          <Divider sx={{ my: 1.5, color: 'text.secondary', fontSize: 13 }}>{t('signup.or')}</Divider>

          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={busy}
            onClick={() => stashAndGo(getSignupUrl(state))}
            sx={{ py: 1.25 }}
          >
            {t('signup.signUpEmail')}
          </Button>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            {t('signup.businessEmailRequired')}
          </Typography>

          <Stack spacing={1.5} sx={{ mt: 4 }}>
            <Typography variant="caption" color="text.secondary">
              <Trans
                i18nKey="signup.alreadyAccount"
                components={{
                  loginLink: (
                    <a href={getLoginUrl()} style={{ color: theme.palette.primary.main, fontWeight: 600 }} />
                  ),
                }}
              />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Trans
                i18nKey="signup.needCustomContract"
                components={{
                  contactLink: (
                    <a href="https://traaker.ai/contact.html" style={{ color: theme.palette.primary.main, fontWeight: 600 }} />
                  ),
                }}
              />
            </Typography>
            {/* The post-auth onboarding derives the account type from ?tier=,
                so a visitor landing here without the agency tier would be
                provisioned as entreprise — point agencies at their funnel. */}
            {tier !== 'agency' && (
              <Typography variant="caption" color="text.secondary">
                <Trans
                  i18nKey="signup.agencyHint"
                  components={{
                    agencyLink: (
                      <a href="https://traaker.ai/tarifs-agences.html" style={{ color: theme.palette.primary.main, fontWeight: 600 }} />
                    ),
                  }}
                />
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled">
              <Trans
                i18nKey="signup.termsAgreement"
                components={{
                  termsLink: (
                    <a href="https://traaker.ai/cgv.html" style={{ color: 'inherit', textDecoration: 'underline' }} />
                  ),
                }}
              />
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* Right — marketing (md+ only) */}
      {wide && <MarketingPanel />}
    </Box>
  );
}

function MarketingPanel() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        flex: '1 1 54%',
        position: 'relative',
        color: 'common.white',
        bgcolor: '#0B0B12',
        backgroundImage:
          'radial-gradient(900px 500px at 85% -10%, rgba(124,58,237,0.28), transparent 60%), ' +
          'linear-gradient(180deg, #0B0B12 0%, #0E0B1A 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { md: 7, lg: 10 },
        py: 8,
      }}
    >
      <Typography variant="h3" fontWeight={800} sx={{ letterSpacing: '-0.03em', lineHeight: 1.05 }}>
        {t('signup.marketing.headlineLine1')}
        <br />
        {t('signup.marketing.headlineLine2')}
        <Box component="span" sx={{ display: 'block', color: '#A78BFA' }}>
          {t('signup.marketing.headlineHighlight')}
        </Box>
      </Typography>

      <Typography sx={{ mt: 3, maxWidth: 460, color: 'rgba(255,255,255,0.72)' }}>
        {t('signup.marketing.subtitle')}
      </Typography>

      {/* Capability stats — capability, not traction (no customer claims yet). */}
      <Stack direction="row" spacing={5} sx={{ mt: 5 }}>
        {[
          { target: 8, suffix: '', l: t('signup.marketing.stat.engines') },
          { target: 11, suffix: '', l: t('signup.marketing.stat.agents') },
          { target: 240, suffix: '+', l: t('signup.marketing.stat.zones') },
        ].map((s) => (
          <Box key={s.l}>
            <Typography variant="h4" fontWeight={800}>
              <CountUp target={s.target} suffix={s.suffix} />
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>{s.l}</Typography>
          </Box>
        ))}
      </Stack>

      <Stack direction="row" sx={{ mt: 6, flexWrap: 'wrap', gap: 1.5 }}>
        {ENGINES.map((e) => (
          <Box
            key={e.label}
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
            title={e.label}
          >
            <Box
              component="img"
              src={e.src}
              alt={e.label}
              sx={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }}
            />
          </Box>
        ))}
      </Stack>

      <Box sx={{ mt: 5 }}>
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          {t('signup.marketing.clients')}
        </Typography>
        <ClientCloud />
      </Box>
    </Box>
  );
}

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Fixed slots, each crossfading through its own shuffled queue on its own
 * randomized timer, so slots never change in sync — a port of the marketing
 * site's `initClientCloud()`. A shared `shown` list keeps two slots from ever
 * displaying the same brand at once.
 */
function ClientCloud() {
  const [logos, setLogos] = useState(() => CLIENT_LOGOS.slice(0, CLIENT_SLOTS));
  const [faded, setFaded] = useState<boolean[]>(() => Array(CLIENT_SLOTS).fill(false));
  const shown = useRef(CLIENT_LOGOS.slice(0, CLIENT_SLOTS).map((l) => l.src));

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    // One fade + one cycle timer per slot, overwritten each round (so the list
    // stays bounded) and cleared on unmount.
    const fadeTimers: number[] = [];
    const cycleTimers: number[] = [];

    for (let idx = 0; idx < CLIENT_SLOTS; idx++) {
      const queue = shuffle(CLIENT_LOGOS);
      let qpos = 0;

      const nextLogo = () => {
        for (let tries = 0; tries < queue.length; tries++) {
          const candidate = queue[qpos % queue.length];
          qpos++;
          if (!shown.current.includes(candidate.src)) return candidate;
        }
        return queue[qpos % queue.length]; // fallback, unreachable with 31 logos / 4 slots
      };

      const cycle = () => {
        setFaded((f) => f.map((v, i) => (i === idx ? true : v)));
        fadeTimers[idx] = window.setTimeout(() => {
          const next = nextLogo();
          shown.current[idx] = next.src;
          setLogos((l) => l.map((v, i) => (i === idx ? next : v)));
          setFaded((f) => f.map((v, i) => (i === idx ? false : v)));
        }, CLIENT_FADE_MS);
        cycleTimers[idx] = window.setTimeout(cycle, CLIENT_FADE_MS + 2600 + Math.random() * 2400);
      };

      cycleTimers[idx] = window.setTimeout(cycle, 800 + Math.random() * 3000);
    }

    return () => {
      [...fadeTimers, ...cycleTimers].forEach(clearTimeout);
    };
  }, []);

  return (
    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px 32px' }}>
      {logos.map((logo, i) => (
        <Box
          key={i}
          sx={{ flexShrink: 0, width: 108, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box
            component="img"
            src={logo.src}
            alt={logo.alt}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              // Dark-ink-on-white assets on a dark panel: see CLIENT_LOGOS.
              filter: 'grayscale(100%) invert(1)',
              opacity: faded[i] ? 0 : 0.8,
              transition: `opacity ${CLIENT_FADE_MS}ms ease`,
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

/** Animates a number from 0 to `target` once on mount (ease-out, ~1.2s). */
function CountUp({ target, suffix = '', duration = 1200 }: { target: number; suffix?: string; duration?: number }) {
  const [value, setValue] = useState(0);
  const frame = useRef<number | undefined>(undefined);

  useEffect(() => {
    let start: number | null = null;
    const step = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setValue(Math.round(eased * target));
      if (p < 1) frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [target, duration]);

  return (
    <>
      {value}
      {suffix}
    </>
  );
}

/** Inline Google "G" so we don't pull an icon dependency. */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

export {
  TIER_STASH_KEY,
  BRAND_STASH_KEY,
  GCLID_STASH_KEY,
  UTM_SOURCE_STASH_KEY,
  UTM_CAMPAIGN_STASH_KEY,
};
